/**
 * Locales understood by the chat iframe for SDK → iframe postMessages.
 */
export type SophiLocale = "tr" | "en" | "pt";

/**
 * Full session envelope posted to the chat iframe (`type: 'session_data'`).
 */
export interface SessionDataChatPayload {
  accessToken: string;
  sessionId: string;
  clientId: string;
  companyCode: string;
  expiresAt: string;
  userName: string;
  /** Present when returned by the session/auth API. */
  layoutId?: string;
  locale?: SophiLocale;
}

/**
 * Payload for `set_locale` — update iframe language without re-sending session.
 */
export interface SetLocalePayload {
  locale: SophiLocale;
}

/**
 * Payload for `set_consent` — tell the iframe to switch its persistence at
 * runtime. `storage: true` promotes the in-memory store to localStorage;
 * `storage: false` demotes back to memory and clears persisted keys.
 */
export interface SetConsentPayload {
  storage: boolean;
}

/**
 * Layout options sent to the iframe on init so the child app can adjust its layout.
 */
export interface LayoutConfig {
  /**
   * When true, show the header; when false, hide it.
   * Omitted/undefined means use the child app's default.
   */
  header?: boolean;
}

/**
 * Configuration for initializing the Sophi Widget
 */
export interface SophiConfig {
  /**
   * API key for authentication with Sophi services (x-api-key header)
   * This comes from the parent application
   */
  apiKey: string;

  /**
   * External user ID for authentication (x-sophi-external-user-id header)
   * This comes from the parent application
   */
  userId?: string;

  /**
   * User name for the user
   */
  userName?: string;

  /**
   * Initial locale for the chat iframe; included on `session_data` and kept for later sends.
   * Use {@link SophiWidget.setLocale} when the host site language changes.
   */
  locale?: SophiLocale;

  /**
   * Container element selector (e.g., '#widget') or HTMLElement where the widget will be mounted
   */
  container: string | HTMLElement;

  /**
   * Optional metadata to send with the session request
   */
  metadata?: Record<string, unknown>;

  /**
   * Width of the widget iframe
   * @default '100%'
   */
  width?: string;

  /**
   * Height of the widget iframe
   * @default '600px'
   */
  height?: string;

  /**
   * Callback fired when the widget is ready
   */
  onReady?: () => void;

  /**
   * Callback fired when an error occurs
   */
  onError?: (error: Error) => void;

  /**
   * Environment to use for the API
   */
  environment?: "test" | "production";

  /**
   * Whether the SDK may persist session data to cookies. Defaults to `true`.
   * Set to `false` to run in anonymous mode: no cookies are written or read,
   * the session lives only in memory, and the user starts fresh on every load.
   * Use {@link SophiWidget.setStorageConsent} to switch to persistent mode at
   * runtime once the user grants cookie consent.
   */
  storageConsent?: boolean;

  /**
   * Layout options sent to the iframe on init. The child app receives these via a
   * postMessage and can show/hide header (and future layout options) accordingly.
   */
  layout?: LayoutConfig;
}

/**
 * Product information for add to cart events
 */
export interface Product {
  /**
   * Unique product identifier
   */
  productId: string;

  /**
   * Optional variant identifier
   */
  variantId?: string;
}

/**
 * Add to cart event data
 */
export interface AddToCartEvent {
  /**
   * List of products to add to cart
   */
  products: Product[];
}

/**
 * Minimal shape shared by both Product and ProductSummary objects
 * sent inside an add_to_favorite message from the child app.
 */
export interface FavoriteProduct {
  /** Internal numeric product ID */
  id: number | string;
  /** External product identifier preferred by the backend favorite API */
  externalId?: string;
  displayName: string;
  images: unknown[];
  favoriteCount: number;
  /** Allows additional fields from full Product or ProductSummary */
  [key: string]: unknown;
}

/**
 * Payload for add_to_favorite postMessage from the iframe (`product` and
 * `isFavorite` match the child app's `sendAddToFavoriteEvent`).
 *
 * The SDK sets `productIdentifier` when forwarding to host listeners:
 * `externalId` if present, otherwise `id`, otherwise `productId`.
 */
export interface AddToFavoritePayload {
  /** Product the user acted on (plain serializable object from the child) */
  product: Product | ProductSummary;
  /** Resulting favorite state after the toggle */
  isFavorite: boolean;
  /** Resolved identifier for host apps (computed by the SDK from `product`) */
  productIdentifier: string;
}

/**
 * Emitted to the host app when an add_to_favorite message is received.
 * Same shape as {@link AddToFavoritePayload}.
 */
export type AddToFavoriteEvent = AddToFavoritePayload;

/**
 * User data that can be sent to the widget
 */
export interface UserData {
  /**
   * User ID
   */
  userId?: string;

  /**
   * User email
   */
  email?: string;

  /**
   * User name
   */
  name?: string;

  /**
   * Additional custom data
   */
  [key: string]: unknown;
}

export interface IUserCartItem {
  productId: string;
  variantId: string;
  quantity?: number;
}

export interface IUserCart {
  cartId: string;
  items: IUserCartItem[];
}

/**
 * Message types that can be received from the iframe
 */
export type IncomingMessageType = "add_to_cart" | "add_to_favorite" | "send_to_checkout" | "ready" | "error";

/**
 * Incoming message type constants for easy access
 * @example
 * widget.on(IncomingMessageTypes.ADD_TO_CART, (data) => {...})
 */
export const IncomingMessageTypes = {
  ADD_TO_CART: "add_to_cart",
  ADD_TO_FAVORITE: "add_to_favorite",
  READY: "ready",
  ERROR: "error",
  SEND_TO_CHECKOUT: "send_to_checkout",
} as const;

/**
 * Message types that can be sent to the iframe
 */
export type OutgoingMessageType =
  | "user_data"
  | "config"
  | "update_user_cart"
  | "destroy"
  | "session_data"
  | "set_locale"
  | "set_consent"
  | "client_id_updated"
  | "layout_config"
  | "toggle_history"
  | "send_text_message"
  | "send_product_reply"
  | "send_outfit_reply"
  | "open_product_by_id"
  | "trigger_product_reply"
  | "update_favorite";

/**
 * Outgoing message type constants for easy access
 * @example
 * widget.sendMessage({ type: OutgoingMessageTypes.USER_DATA, data: {...} })
 */
export const OutgoingMessageTypes = {
  USER_DATA: "user_data",
  CONFIG: "config",
  UPDATE_USER_CART: "update_user_cart",
  DESTROY: "destroy",
  SESSION_DATA: "session_data",
  SET_LOCALE: "set_locale",
  SET_CONSENT: "set_consent",
  CLIENT_ID_UPDATED: "client_id_updated",
  LAYOUT_CONFIG: "layout_config",
  TOGGLE_HISTORY: "toggle_history",
  SEND_TEXT_MESSAGE: "send_text_message",
  SEND_PRODUCT_REPLY: "send_product_reply",
  SEND_OUTFIT_REPLY: "send_outfit_reply",
  OPEN_PRODUCT_BY_ID: "open_product_by_id",
  TRIGGER_PRODUCT_REPLY: "trigger_product_reply",
  UPDATE_FAVORITE: "update_favorite",
} as const;

/**
 * Payload sent from the host to the iframe to sync a favorite state change
 * that originated outside the iframe (e.g. ProductCard toggle in the host app).
 */
export interface UpdateFavoritePayload {
  /** String product ID matching the product's `id` or `externalId` in the iframe. */
  productId: string;
  /** New favorite state to apply in the iframe. */
  isFavorite: boolean;
}

/**
 * Incoming message structure from iframe
 */
export interface IncomingMessage {
  type: IncomingMessageType;
  data?: unknown;
}

/**
 * Outgoing message structure to iframe
 */
export interface OutgoingMessage {
  type: OutgoingMessageType;
  data?: unknown;
}

/**
 * Event map for type-safe event handling
 */
export interface SophiEventMap {
  /**
   * Fired when products should be added to cart
   */
  add_to_cart: AddToCartEvent;

  /**
   * Fired when the user wants to add a product to their favourites.
   * productIdentifier is pre-resolved as externalId ?? id.
   */
  add_to_favorite: AddToFavoriteEvent;

  /**
   * Fired when the widget is ready
   */
  ready: void;

  /**
   * Fired when an error occurs
   */
  error: Error;

  /**
   * Fired when the user should be sent to checkout
   */
  send_to_checkout: void;
}

/**
 * Event handler type
 */
export type EventHandler<T> = (data: T) => void;

/**
 * Event name type
 */
export type EventName = keyof SophiEventMap;

/**
 * Authentication session request payload
 */
export interface AuthSessionRequest {
  /**
   * External user ID from the parent application
   */
  externalUserId?: string;

  /**
   * Optional metadata to include with the session
   */
  metadata?: Record<string, unknown>;
}

/**
 * Session data returned from the authentication API
 */
export interface SessionData {
  /**
   * JWT access token for authenticating requests
   */
  accessToken: string;

  /**
   * Unique session identifier
   */
  sessionId: string;

  /**
   * Client ID
   */
  clientId: string;

  /**
   * Company code
   */
  companyCode: string;

  /**
   * Layout identifier for the chat experience (when returned by the API).
   */
  layoutId?: string | number;

  /**
   * Session expiration timestamp
   */
  expiresAt: string;

  /**
   * userName of the user
   */
  userName: string;
}

/**
 * Authentication session API response
 */
export interface AuthSessionResponse {
  /**
   * Whether the request was successful
   */
  success: boolean;

  /**
   * Response message
   */
  message: string;

  /**
   * Session data
   */
  data?: SessionData;
}

/**
 * Merge user request payload
 */
export interface MergeUserRequest {
  /**
   * The logged-in user ID to merge to
   */
  userId: string;
}

/**
 * Merge user data returned from the API
 */
export interface MergeUserData {
  /**
   * The resulting client ID after merge
   */
  clientId: string;

  /**
   * The external user ID
   */
  externalUserId: string;
}

/**
 * Merge user API response
 */
export interface MergeUserResponse {
  /**
   * Whether the request was successful
   */
  success: boolean;

  /**
   * Response message
   */
  message: string;

  /**
   * Merge result data
   */
  data?: MergeUserData;
}

// ---------------------------------------------------------------------------
// Chat message types
// ---------------------------------------------------------------------------

/**
 * A single product image as returned by the recommendation API.
 */
export interface ProductImage {
  url: string;
  isPrimary: boolean;
  sortOrder: number;
  altText: string | null;
}

/**
 * Pricing information attached to a product's best available offer.
 */
export interface ProductOffer {
  listPrice: number;
  salePrice: number;
  discountRate: number | null;
  currency: string;
}

/**
 * Summary shape of a product as returned inside outfit/recommendation responses.
 */
export interface ProductSummary {
  id: number;
  externalId: string;
  displayName: string;
  shortDescription: string;
  images: ProductImage[];
  rating: number;
  favoriteCount: number;
  bestOffer: ProductOffer;
  brandName: string;
  isAvailable: boolean;
  totalStock: number;
}

/**
 * A single item inside an outfit recommendation.
 */
export interface OutfitItem {
  productId: number;
  reason: string;
  recommendationId: number;
  detail: ProductSummary;
}

/**
 * Full outfit recommendation response from the Sophi API.
 */
export interface OutfitResponse {
  recommendationId: number;
  outfitName: string;
  items: OutfitItem[];
}

/**
 * Payload for a plain chat message with no product or outfit context.
 */
export interface SendTextMessagePayload {
  query: string;
}

/**
 * Optional context for {@link SophiWidget.sendProductReply} when the product
 * is part of an outfit recommendation.
 */
export interface ProductReplyOptions {
  /** Set to `true` when the query involves outfit creation / viewing context. */
  isOutfit?: boolean;
  query?: string;
}

/**
 * Payload posted to the iframe for a product-contextual chat message.
 */
export interface SendProductReplyPayload {
  query?: string;
  externalProductId: string;
  isOutfit?: boolean;
}

/**
 * Payload posted to the iframe for an outfit-contextual chat message.
 */
export interface SendOutfitReplyPayload {
  query: string;
  outfitId: number;
  outfit: OutfitResponse;
}

// ---------------------------------------------------------------------------
// UI-control message types
// ---------------------------------------------------------------------------

/**
 * Payload that instructs the iframe to fetch and open the product detail panel
 * for the given product ID, exactly as if the user clicked a product card.
 */
export interface OpenProductByIdPayload {
  /** Internal numeric product ID to open. */
  productId: number;
  /** Optional recommendation reason text shown alongside the product. */
  reason?: string;
}

/**
 * Payload that sets a product as the active reply context in the chat input bar
 * (the "reply popover" above the input), exactly as if the user clicked
 * "Ask a Question" on a product card.
 */
export interface TriggerProductReplyPayload {
  /** Full ProductSummary object to set as the reply context. */
  product: ProductSummary;
}
